/**
 * The server's readiness for the programme open in the editor.
 *
 * The editor bar and the pack dialog both label the programme by the rung the
 * server evaluates, so one read feeds both rather than each guessing from the
 * client's own completion count.
 *
 * @module platform/editor-readiness-context
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { ReadinessResult } from "@/server/types/readiness";

import type { ProgrammePlatformClient } from "./client";

/** Autosave lands every 400ms while someone types; readiness need not follow each one. */
export const READINESS_REFRESH_MS = 2000;

const EditorReadinessContext = createContext<{ result: ReadinessResult | null; failed: boolean }>({
  result: null,
  failed: false,
});

export function EditorReadinessProvider({
  value,
  failed = false,
  children,
}: {
  value: ReadinessResult | null;
  /** The last read failed, so a null `value` is no answer rather than none yet. */
  failed?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const shared = useMemo(() => ({ result: value, failed }), [failed, value]);
  return (
    <EditorReadinessContext.Provider value={shared}>{children}</EditorReadinessContext.Provider>
  );
}

/** The open programme's readiness, or null before it is read or outside the editor. */
export function useEditorReadiness(): ReadinessResult | null {
  return useContext(EditorReadinessContext).result;
}

/** Whether the last readiness read failed, as opposed to not having answered yet. */
export function useEditorReadinessFailed(): boolean {
  return useContext(EditorReadinessContext).failed;
}

// The shell's sidebar sits above the editor that reads readiness, so the
// editor publishes its answer up to it rather than reading it twice.
const PublishedReadinessContext = createContext<{
  value: ReadinessResult | null;
  publish: (value: ReadinessResult | null) => void;
} | null>(null);

export function PublishedReadinessProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [value, publish] = useState<ReadinessResult | null>(null);
  const shared = useMemo(() => ({ value, publish }), [value]);
  return (
    <PublishedReadinessContext.Provider value={shared}>
      {children}
    </PublishedReadinessContext.Provider>
  );
}

/** The readiness the open editor last published, or null outside one. */
export function usePublishedReadiness(): ReadinessResult | null {
  return useContext(PublishedReadinessContext)?.value ?? null;
}

/** Hands the editor's readiness to the shell, and withdraws it on leaving. */
export function usePublishReadiness(result: ReadinessResult | null): void {
  const publish = useContext(PublishedReadinessContext)?.publish;
  useEffect(() => {
    publish?.(result);
    return () => publish?.(null);
  }, [publish, result]);
}

export interface ServerReadinessOptions {
  client: Pick<ProgrammePlatformClient, "getProgrammeGovernance">;
  programmeId: string | undefined;
  /** Each new revision is a reason to read again. */
  saveRevision: number | undefined;
  enabled: boolean;
}

export interface ServerReadiness {
  /** The last answer, or null before one arrives. */
  result: ReadinessResult | null;
  /** The last read failed, so `result` may describe an earlier revision. */
  failed: boolean;
  /** Reads again now rather than at the next save. */
  retry: () => void;
  /** A read asked for by hand is out, so the control that asked can say so. */
  retrying: boolean;
}

/**
 * Reads the programme's readiness, again after each save, at most once every
 * {@link READINESS_REFRESH_MS}. A save inside the window is read at its end.
 */
export function useServerReadiness({
  client,
  programmeId,
  saveRevision,
  enabled,
}: ServerReadinessOptions): ServerReadiness {
  const [read, setRead] = useState<{ programmeId: string; result: ReadinessResult } | null>(null);
  const [failedFor, setFailedFor] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const lastStarted = useRef(Number.NEGATIVE_INFINITY);
  const started = useRef(0);
  const accepted = useRef(0);
  // The last read started before Try again was pressed. Only a later one is
  // the answer `retrying` waits for.
  const retriedAfter = useRef(Number.POSITIVE_INFINITY);

  useEffect(() => {
    if (!enabled || !programmeId) {
      return undefined;
    }
    const wait = Math.max(0, lastStarted.current + READINESS_REFRESH_MS - Date.now());
    const timer = setTimeout(() => {
      lastStarted.current = Date.now();
      started.current += 1;
      const sequence = started.current;
      // A slower earlier answer, either way, must not replace a newer one.
      client
        .getProgrammeGovernance(programmeId)
        .then((summary) => {
          if (sequence > accepted.current) {
            accepted.current = sequence;
            setRead({ programmeId, result: summary.readiness });
            setFailedFor(null);
          }
          if (sequence > retriedAfter.current) {
            setRetrying(false);
          }
        })
        .catch(() => {
          if (sequence > accepted.current) {
            accepted.current = sequence;
            setFailedFor(programmeId);
          }
          if (sequence > retriedAfter.current) {
            setRetrying(false);
          }
        });
    }, wait);
    return () => clearTimeout(timer);
  }, [attempt, client, enabled, programmeId, saveRevision]);

  const retry = useCallback(() => {
    lastStarted.current = Number.NEGATIVE_INFINITY;
    retriedAfter.current = started.current;
    // Cleared as the read is asked for: the failure being retried is no longer
    // what the screen is reporting, and `retrying` is what replaces it.
    setFailedFor(null);
    setRetrying(true);
    setAttempt((current) => current + 1);
  }, []);

  return {
    result: enabled && read && read.programmeId === programmeId ? read.result : null,
    failed: enabled && failedFor !== null && failedFor === programmeId,
    retry,
    // Switching off clears the read a retry was waiting for.
    retrying: enabled && retrying,
  };
}

/** Whether a pack made now is the final one rather than a draft. */
export function isReadyForFinalExport(readiness: ReadinessResult | null): boolean {
  return (
    readiness?.state === "Ready for final export" || readiness?.state === "Exported and locked"
  );
}
